import cx from 'classnames';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useEffect } from 'react';
import { FunctionComponent, useMemo } from 'react';

import useLinkProps from '../../../hooks/use-link-props';
import useLoggedUser from '../../../hooks/use-logged-user';
import SearchBadges from '../../modules/legacy/search-badges';
import { useOpenSearch } from '../../modules/search/state';
import FeatureLink from '../../ui/links/featured';
import OrSeparator from '../../ui/v2/separator';

import WarningMorePartsWihoutCar from './more-parts-without-car';
import { selectVehicleStepAtom, vehiclePickerOpen } from '../../../state/vehicle';
import { useAtom } from 'jotai';
import { brandAtom, modelAtom, partAtom, referenceAtom, versionAtom } from '../../../hooks/use-picker-selection';
import { useAppContext } from '../../providers/app-context';
import SetAlertInfo from '../v2/create-part-alert/index_new';

const Empty: FunctionComponent<{
  search: TProductSearch['search'];
  others_with_reference: TProductSearch['others_with_reference'];
  totalResults: TProductSearch['results_total'];
  vehicleData?: any;
}> = ({ search, others_with_reference, totalResults, vehicleData }) => {
  const { tenantConfig, brand } = useAppContext();
  const [, setVehiclePicker] = useAtom(vehiclePickerOpen);
  const [, setSelectedVehicleStep] = useAtom(selectVehicleStepAtom);
  const [, setPart] = useAtom(partAtom);
  const [, setReference] = useAtom(referenceAtom);
  const [, setBrandAtom] = useAtom(brandAtom);
  const [, setModelAtom] = useAtom(modelAtom);
  const [, setVersionAtom] = useAtom(versionAtom);
  const { t } = useTranslation();
  const getLink = useLinkProps();
  const user = useLoggedUser();
  const openSearch = useOpenSearch();

  useEffect(() => {
    window.dataLayer.push({
      event: 'zeroResults',
      partName: search?.part?.part_en || null,
      oem: search.reference || null, // product reference
      brand: search.car?.brand_name || null, // car brand
      model: search.car?.model_name || null, // car model
      version: search.car?.version_name || null, // car version
    });
  }, []);

  const carCompleteName = useMemo(
    () =>
      search.car &&
      `${search.car.brand_name} ${search.car.model_name}${search.car.version_name ? ` ${search.car.version_name}` : ''
      }`,
    [search.car]
  );

  return (
    <div className="lg:px-0">
      <p className="font-extrabold text-[32px] mb-2">
        {t('products:incomplete-search')}
      </p>
      <p className="mb-2">
        {search.reference ? (
          search.part?.name && search.car?.brand_name ? (
            <Trans
              i18nKey="products:no-result.part-car-ref"
              components={{ span: <span className={cx('font-bold')} /> }}
              values={{
                reference: search.reference,
                part: search.part.name,
                car: carCompleteName,
              }}
            />
          ) : search.car?.brand_name ? (
            <Trans
              i18nKey="products:no-result.car-ref"
              components={{ span: <span className={cx('font-bold')} /> }}
              values={{
                reference: search.reference,
                car: carCompleteName,
              }}
            />
          ) : (
            <Trans
              i18nKey="products:no-result.ref"
              components={{ span: <span className={cx('font-bold')} /> }}
              values={{
                reference: search.reference,
              }}
            />
          )
        ) : (
          <Trans
            i18nKey="products:no-result.part-car"
            components={{ span: <span className={cx('font-bold')} /> }}
            values={{
              part: search?.part?.name || '',
              car: carCompleteName,
            }}
          />
        )}
      </p>
      <SetAlertInfo
        brand={vehicleData?.info?.brand || brand}
        model={vehicleData?.info?.model}
        version={vehicleData?.info?.version}
        part={search?.part}
        reference={search?.reference} />
    </div>
  );
};

export default Empty;
